/**
 * Hallmark · pure-domain-logic · PDU form adaptation
 * Preset generation, attribute building, and parsing for PDU configuration.
 */

export interface PduOutletSpecItem {
  count: number;
  type: string;
}

export interface PduOutletPreset {
  id: string;
  label: string;
  totalCount: number;
  spec: PduOutletSpecItem[];
}

export const PDU_FEED_OPTIONS = [
  'Feed A',
  'Feed B',
  'Feed C',
] as const;

export const PDU_PHASE_OPTIONS = [
  { value: 'phase_a', label: 'Фаза A (L1)' },
  { value: 'phase_b', label: 'Фаза B (L2)' },
  { value: 'phase_c', label: 'Фаза C (L3)' },
  { value: 'three_phase', label: '3-фазный ввод' },
] as const;

export const PDU_AMPERAGE_OPTIONS = [16, 32] as const;

export const PDU_OUTLET_PRESETS: PduOutletPreset[] = [
  {
    id: '24_c13',
    label: '24 × C13 (10A)',
    totalCount: 24,
    spec: [{ count: 24, type: 'C13' }],
  },
  {
    id: '20_c13_4_c19',
    label: '20 × C13 + 4 × C19 (16A)',
    totalCount: 24,
    spec: [
      { count: 20, type: 'C13' },
      { count: 4, type: 'C19' },
    ],
  },
  {
    id: '16_c13',
    label: '16 × C13',
    totalCount: 16,
    spec: [{ count: 16, type: 'C13' }],
  },
  {
    id: '12_c13',
    label: '12 × C13',
    totalCount: 12,
    spec: [{ count: 12, type: 'C13' }],
  },
  {
    id: '8_c13',
    label: '8 × C13',
    totalCount: 8,
    spec: [{ count: 8, type: 'C13' }],
  },
];

export interface PduConfigValues {
  feedName: string;
  phase: string;
  ratedAmperage: number;
  presetId: string;
}

export function buildPduAttributes(config: PduConfigValues): string {
  const preset = PDU_OUTLET_PRESETS.find(p => p.id === config.presetId) || PDU_OUTLET_PRESETS[0];
  const primaryType = preset.spec[0]?.type || 'C13';

  const obj = {
    feed_name: config.feedName,
    phase: config.phase,
    rated_current_a: Number(config.ratedAmperage),
    outlets_count: preset.totalCount,
    outlet_type: primaryType,
    outlets_spec: preset.spec,
  };

  return JSON.stringify(obj, null, 2);
}

export function parsePduAttributes(rawJson: string | Record<string, any> | null | undefined): PduConfigValues {
  let parsed: any = {};
  if (rawJson) {
    if (typeof rawJson === 'object') {
      parsed = rawJson;
    } else if (typeof rawJson === 'string') {
      try {
        parsed = JSON.parse(rawJson);
      } catch {
        parsed = {};
      }
    }
  }

  const feedName = parsed.feed_name || 'Feed A';
  const phase = parsed.phase || 'phase_a';
  const ratedAmperage = Number(parsed.rated_current_a) || 32;

  // Resolve preset from outlets_spec or outlets_count
  let presetId = '24_c13';
  if (Array.isArray(parsed.outlets_spec) && parsed.outlets_spec.length > 0) {
    const isMixed20_4 =
      parsed.outlets_spec.length === 2 &&
      parsed.outlets_spec.some((s: any) => s.count === 20 && s.type === 'C13') &&
      parsed.outlets_spec.some((s: any) => s.count === 4 && s.type === 'C19');
    if (isMixed20_4) {
      presetId = '20_c13_4_c19';
    } else {
      const total = parsed.outlets_spec.reduce((sum: number, s: any) => sum + (Number(s.count) || 0), 0);
      if (total === 16) presetId = '16_c13';
      else if (total === 12) presetId = '12_c13';
      else if (total === 8) presetId = '8_c13';
      else presetId = '24_c13';
    }
  } else if (parsed.outlets_count) {
    const cnt = Number(parsed.outlets_count);
    if (cnt === 16) presetId = '16_c13';
    else if (cnt === 12) presetId = '12_c13';
    else if (cnt === 8) presetId = '8_c13';
    else presetId = '24_c13';
  }

  return {
    feedName,
    phase,
    ratedAmperage,
    presetId,
  };
}
